import React, { MouseEvent } from 'react';
import { shallowEqual } from 'react-redux';
import { Box, Spinner } from '@apia/theme';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import Icon from '../../Icon';
import DefaultIconRenderer from './renderers/DefaultIconRenderer';
import DefaultLabelRenderer from './renderers/DefaultLabelRenderer';
import Spacer from './renderers/Spacer';
import { useTreeContext } from './TreeContext';
import { TDataNode, TId } from './types';

const TreeItemLabel = ({ level, treeKey }: { level: number; treeKey: TId }) => {
  const {
    handler,
    treeProps: { toggleNodesOnLabelClick, disableExpand },
  } = useTreeContext();

  const props = usePropsSelector<
    TDataNode<Record<string, unknown>>,
    TDataNode<Record<string, unknown>>
  >(treeKey, {
    selector: (current) => current,
    comparator: (prevProps, newProps) => {
      return (
        shallowEqual(prevProps, newProps) &&
        shallowEqual(prevProps?.children, newProps?.children)
      );
    },
    propsStore: handler.propsStore,
  });

  const Renderer = React.useMemo(
    () => props.labelRenderer ?? DefaultLabelRenderer,
    [props.labelRenderer],
  );

  const handleToggle = React.useCallback(
    (ev: MouseEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      handler.toggleNodeExpandedState(treeKey);
    },
    [handler, treeKey],
  );

  const IconRenderer = React.useMemo(() => {
    return typeof props.icon === 'string' ? DefaultIconRenderer : props.icon;
  }, [props.icon]);

  const onClick = React.useCallback(() => {
    if (props.allowToggleExpandedFromLabel !== false)
      handler.toggleNodeExpandedState(treeKey);
  }, [handler, props.allowToggleExpandedFromLabel, treeKey]);

  return (
    <Box
      as="span"
      className={`tree__nodeItemLabel ${props.isFocused ? 'focus' : ''}`}
    >
      <Spacer level={level} />
      {(props.isLoading ||
        (!disableExpand &&
          ((props.isExpanded && props.isLeaf !== true) ||
            props.isLeaf === false ||
            (props.children?.length ?? 0) > 0 ||
            (props.isLeaf === undefined &&
              !props.hasLoaded &&
              handler.configuration.onLoadData)))) && (
        <Box className="tree__expanderWrapper">
          {props.isLoading ? (
            <Spinner sx={{ width: 'iconSm', height: 'iconSm' }} />
          ) : (
            <Icon
              className="tree__expandIcon"
              onClick={handleToggle}
              name={props.isExpanded ? 'MinusFilled' : 'PlusFilled'}
              size="iconSm"
            />
          )}
        </Box>
      )}
      {props.icon && IconRenderer && <IconRenderer {...props} />}
      <Box
        as="span"
        className="tree__nodeItemLabelRenderer"
        onClick={toggleNodesOnLabelClick !== false ? onClick : undefined}
      >
        <Renderer {...props} level={level} />
      </Box>
    </Box>
  );
};

export default React.memo(TreeItemLabel);
